import { Box, TypographyStylesProvider } from "@mantine/core";
import React from "react";
import { IconDatabase } from "@tabler/icons-react";
import DocsButton from "../articlesHelpers/DocsButton";
import { JsonView, allExpanded, darkStyles } from "react-json-view-lite"; // Add the missing imports

export const NODE_DATA_CONTENT = (
  <TypographyStylesProvider>
    <p>With this button, you can access some raw data about your node.</p>
    <DocsButton
      ariaLabel="Node Data button"
      text="Node Data"
      leftSection={<IconDatabase />}
    />
    <p>You will see something like this:</p>
    <Box mb="20px">
      <JsonView
        data={{
          chains: [
            "6fe28c0ab6f1b372c1a6a246ae63f74f931e8365e15a089c68d6190000000000",
          ],
          color: "#3399ff",
          active_channels_count: 1,
          alias: "DEV-PRIVATE-INBOUND",
          current_block_hash:
            "00000000000000000000742685078a852fd57cfae0d982a27e76510709a0ad8f",
          current_block_height: 870673,
          features: [],
          is_synced_to_chain: true,
          is_synced_to_graph: true,
          latest_block_at: "2024-11-17T05:28:23.000Z",
          peers_count: 2,
          pending_channels_count: 0,
          public_key:
            "03c2160ebbad3c18156bf613598a54ad3147ae4ea3916a2c8a3983680e7b99e3e1",
          uris: [
            "03c2160ebbad3c18156bf613598a54ad3147ae4ea3916a2c8a3983680e7b99e3e1@rex4zwmuwmv7ftnsf37p3giydqvhmpuzzcpjezhpxjjm26j7neeoxjad.onion:9735",
          ],
          version: "",
          timestamp: 1731822083395,
        }}
        shouldExpandNode={allExpanded}
        style={darkStyles}
      />
    </Box>
    <p>Here are some explanations of the fields you might find relevant:</p>
    <ul>
      <li>
        <b>chains:</b> A list of blockchain IDs the node is connected to. This
        Rizful Node$1 one represents the Bitcoin blockchain, but if you're
        running on Rizful on Testnet, you'll see a different chain ID.
      </li>
      <li>
        <b>color:</b> The visual color assigned to the node in graphical
        interfaces. This could show up on lightning explorers, but it's really
        not used that much in practice.
      </li>
      <li>
        <b>active_channels_count:</b> The number of currently active channels
        that your node has.
      </li>
      <li>
        <b>alias:</b> The human-readable name of the node, often used for
        identification. See our notes on that{" "}
        <a href="/docs/lightning-node-basics#node-info-and-alias"> here</a>.
      </li>
      <li>
        <b>current_block_hash:</b> The hash of the latest block in the Bitcoin
        blockchain that this node has synchronized. You'll never use this in
        practice, but it's interesting to know.
      </li>
      <li>
        <b>current_block_height:</b> The height of the current block this node
        recognizes on the blockchain.
      </li>
      <li>
        <b>is_synced_to_chain:</b> Indicates whether the node is fully synced
        with the Bitcoin blockchain. If your Rizful node is stopped due to
        inactivity, when you manually start it, this will be <code>false</code>{" "}
        for a while while the node synchronizes. This is normal.
      </li>
      <li>
        <b>is_synced_to_graph:</b> Indicates whether the node is fully synced
        with the Lightning Network graph. Again, if your Rizful node is stopped
        due to inactivity, when you manually start it, this will be{" "}
        <code>false</code> for a while while the node synchronizes. This is the
        synchronization step which can sometimes take a while: Your node has to
        look both at the blockchain AND at the gossip data it gets from other
        nodes, and make it's **own** decisions about what the network looks like
        -- most critically, which channels between which nodes are still open,
        and which have closed.
      </li>

      <li>
        <b>peers_count:</b> The number of peers currently connected to this
        node. Peers are critical, because they provide the "gossip" data to your
        node -- info about where all the channels are on the Lightning Network.
        When you're first starting with lightning, it might be tempting to think
        that you need lots of peers. But in practice, you only need a few, and
        any nodes that you make channels with will automatically be maintained
        as gossip peers.
      </li>
      <li>
        <b>pending_channels_count:</b> The number of channels that are not yet
        active. This could be because they are in the process of opening, or the
        process of closing.
      </li>
      <li>
        <b>public_key:</b> See our description{" "}
        <a href="/docs/lightning-node-basics#node-info-and-alias"> here</a>.
      </li>
      <li>
        <b>uris:</b> See our description{" "}
        <a href="/docs/lightning-node-basics#node-info-and-alias"> here</a>.
      </li>
    </ul>
  </TypographyStylesProvider>
);
